import React, { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'

import { BackButton, Logo, Spinner } from '../App/Components'
import {
  ListContainer,
  OptionsContainer,
  StyledScrollView,
  TileContainer,
} from '../App/Components/AppContainer'
import { LastMinutesItem } from '../LastMinutes/LastMinutesItem/LastMinutesItem'
import { Redirect, useLocation, useParams } from '../Router'

import { ListStructure, TileStructure } from './Components'
import { StyledTitle } from './style'
import { useAppSelector } from '../store/hooks'
import { WorkshopItem } from '../Workshop/WorkshopItem/WorkshopItem'
import { PunchCardItem } from '../PunchCard/Components/PunchCardItem/PunchCardItem'
import { ListStructureCart } from '../App/Components/ListStructureCart/ListStructureCart'
import { Chat } from '../Chat'
import { DEFAULT_ENDPOINTS } from '../Chat/defaultEndpoints'
import { getTranslatedTexts } from '../Chat/defaultTexts'
import { getUserData } from '../App/Util/localStorageHelper/UserData/getUserData'
import { setUserData } from '../App/Util/localStorageHelper/UserData/setUserData'
import { initialUserData } from '../App/Util/localStorageHelper/UserData/IUserData'

export const ExtraActivity = () => {
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const { establishmentId } = useParams<{ [key: string]: string }>()

  const settings = useAppSelector((state) => state.settings.values)
  const userData = getUserData()
  const appointment = useAppSelector((state) => state.appointment)
  const hasActivities: boolean =
    appointment.customers.reduce(
      (previousValue, currentValue) =>
        previousValue + currentValue.activities.length,
      0
    ) > 0
  const rootPath = `/${establishmentId}/activities`
  const isRootPath: boolean =
    location.pathname === rootPath || location.pathname === `${rootPath}/`

  const { isListView } = settings
  const StructureContainer: FunctionComponent = isListView
    ? ListContainer
    : TileContainer

  const isActivityList = location.pathname.includes('activities') && isListView

  let backUrl: string | undefined

  if (typeof settings === 'undefined') {
    return <Spinner />
  }

  // A menu-only salon must never reach the loose-service (/activities) flow via a
  // cold deep-link. It is only legitimate as an "add extra activity" step, which
  // requires extras to be enabled AND a menu already in progress (hasActivities).
  // Otherwise redirect to the menu — the correct entry point for these salons.
  const isExtraActivityAllowed =
    !settings.isMenuVisible ||
    (settings.isAddExtraActivityVisible && hasActivities)

  if (!isExtraActivityAllowed) {
    return <Redirect to={`/${establishmentId}/menu`} />
  }

  const onlineBookableLocations = settings.locations.filter(
    (locationCheck) => locationCheck.onlineBookingEnabled
  )

  if (isRootPath) {
    if (
      onlineBookableLocations.length > 1 &&
      settings.isSelectLocation &&
      !hasActivities
    ) {
      backUrl = `/${establishmentId}/select-location`
    }
  } else {
    backUrl = rootPath
  }

  return (
    <>
      <StyledScrollView>
        <Logo />
        <StyledTitle highlight={settings.fontColor}>
          {t('Please select a service')}
        </StyledTitle>

        {isActivityList ? null : (
          <BackButton rootPathCheck={isRootPath} customPath={backUrl} />
        )}
        <StructureContainer>
          {/*
           * Last minutes, workshops and punch cards are standalone flows that do
           * not go through the appointment cart: booking one only submits its own
           * id (or, for punch cards, navigates out to the Customer Portal), so the
           * services already selected would be silently discarded. Only offer them
           * as a top-level entry point (empty cart) — never as an "add extra
           * activity" step, where a booking is already in progress (hasActivities).
           */}
          {!hasActivities && (
            <>
              <LastMinutesItem isListView={isListView} />
              <WorkshopItem display={isListView ? 'list' : 'tile'} />
              <PunchCardItem display={isListView ? 'list' : 'tile'} />
            </>
          )}
          {isListView ? <ListStructure /> : <TileStructure />}
        </StructureContainer>
        <OptionsContainer />
      </StyledScrollView>

      {settings.aiBookingAssistantEnabled && settings.uuid && (
        <Chat
          establishmentUuid={settings.uuid}
          apiEndpoints={DEFAULT_ENDPOINTS}
          logo={settings.logo}
          themeColors={{
            primary: settings.highlightColor,
            botBackground: settings.backgroundColor,
            botTextColor: settings.fontColor,
          }}
          userData={{
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            phone: userData.phone,
          }}
          onClearUserData={() => setUserData(initialUserData)}
          texts={getTranslatedTexts(t)}
          language={i18n.language}
          bubblePosition="left"
        />
      )}

      {isActivityList ? <ListStructureCart /> : null}
    </>
  )
}
